import { FC } from 'react';
import { Svg } from '@altermaind/web/ui';
import { StyledSwitch, SwitchThumb } from './styles';
import { SwitchProps } from './types';
import { useSwitchStyle } from './useSwitchStyle';

/**
 * Switch
 * Generic, controlled toggle meant to be used **in place of the Design System
 * `Switch` from `@altermaind/web/ui`** across the app. It renders only track +
 * sliding thumb; all styling comes from the theme (`switch.ts`) via the
 * `variant`, so specific switches (e.g. day/night) are created by adding a
 * variant family in the theme rather than passing style props.
 */
export const Switch: FC<SwitchProps> = ({
  isChecked = false,
  onClick,
  name,
  variant = 'classic',
  gap = 2,
  iconUnchecked,
  iconChecked,
  iconUncheckedColor,
  iconCheckedColor,
  isDisabled = false,
  'data-testid': dataTestId = 'Switch',
  thumbStyle,
  ...rest
}) => {
  const { size: userThumbSize, ...restThumbStyle } = thumbStyle ?? {};
  const {
    effectiveVariant,
    translateX,
    thumbSize: computedThumbSize,
  } = useSwitchStyle(variant, isChecked, gap, userThumbSize);

  return (
    <StyledSwitch
      variant={effectiveVariant}
      isChecked={isChecked}
      isDisabled={isDisabled}
      aria-checked={isChecked}
      aria-label={name}
      onClick={isDisabled ? undefined : onClick}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      <SwitchThumb
        variant={effectiveVariant}
        translateX={translateX}
        data-testid={`${dataTestId}-thumb`}
        size={computedThumbSize}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...restThumbStyle}
      >
        {iconChecked && isChecked && <Svg name={iconChecked} size='s' fill={iconCheckedColor} />}
        {iconUnchecked && !isChecked && (
          <Svg name={iconUnchecked} size='s' fill={iconUncheckedColor} />
        )}
      </SwitchThumb>
    </StyledSwitch>
  );
};
